// Preventive maintenance setup — the whole IP41/IA05/IP10 chain from the portal:
// task lists (WHAT gets done), plans (HOW OFTEN), scheduling (orders appear). Third tab is
// equipment master data + counter readings (EquipmentPanel). Gated on canManagePreventive
// (tile-level) and on me.sap.capabilities: a SAP release without MPLAN_API/EAM_TL_API gets an
// honest hint instead of forms whose submit can only 501.
import React, { useState, useEffect } from 'react';
import { ArrowLeft, CalendarClock, Plus, PlayCircle, PauseCircle } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { Card, Button, Row } from './ui';
import EquipmentPanel from './EquipmentPanel';
import CountersPanel from './CountersPanel';
import { listPlans, getPlan, createPlan, schedulePlan, setPlanActive, listTaskLists, createTaskList } from '../api';

const input = (isDarkMode) =>
  `w-full rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/10 text-white placeholder-white/40' : 'bg-slate-100 text-slate-800 placeholder-slate-400'}`;

/** Plans tab: list (+expandable detail with calls), create form, schedule/de-/activate. */
function PlansTab({ me, user, capabilities }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const role = me.profile.id;
  const scope = me.profile.scope;

  const [plans, setPlans] = useState(null);
  const [taskLists, setTaskLists] = useState([]);
  const [openPlan, setOpenPlan] = useState(null);   // { planId, detail|null }
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ description: '', cycleDays: '30', taskListGroup: '', equipmentId: '', workCenter: scope.workCenters[0], startDate: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const canCreate = !capabilities || capabilities.planCreate !== false;
  const canSchedule = !capabilities || capabilities.planScheduling !== false;

  useEffect(() => {
    let alive = true;
    setPlans(null);
    Promise.all([
      listPlans(user, me.activePlant, role),
      listTaskLists(user, me.activePlant, role),
    ]).then(([p, tl]) => {
      if (!alive) return;
      setPlans(p.items || []);
      const lists = tl.items || [];
      setTaskLists(lists);
      setForm((f) => ({ ...f, taskListGroup: f.taskListGroup || (lists[0] && lists[0].group) || '' }));
    }).catch(() => { if (alive) setPlans([]); });
    return () => { alive = false; };
  }, [user, me.activePlant, role, reloadKey]);

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (res && res.ok === false) { setError(res.error || t('pm.prev.actionFailed')); return null; }
      setReloadKey((k) => k + 1);
      return res;
    } catch (e) { setError(e.message); return null; } finally { setBusy(false); }
  };

  const toggleDetail = async (planId) => {
    if (openPlan && openPlan.planId === planId) { setOpenPlan(null); return; }
    setOpenPlan({ planId, detail: null });
    try {
      const d = await getPlan(user, planId, role);
      setOpenPlan((cur) => (cur && cur.planId === planId ? { planId, detail: d } : cur));
    } catch { setOpenPlan(null); }
  };

  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  return (
    <div>
      {error && <Card className="mb-3"><p className={isDarkMode ? 'text-rose-300 text-sm' : 'text-rose-600 text-sm'}>{error}</p></Card>}

      {canCreate && (
        <Card className="mb-3">
          <button type="button" className={`flex items-center gap-2 font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
            onClick={() => setCreating((v) => !v)}>
            <Plus size={18} /> {t('pm.prev.newPlan')}
          </button>
          {creating && (
            <div className="mt-3 space-y-2">
              <input className={input(isDarkMode)} placeholder={t('pm.prev.planDescription')} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div className="flex gap-2">
                <input className={input(isDarkMode)} type="number" min="1" placeholder={t('pm.prev.cycleDays')} value={form.cycleDays}
                  onChange={(e) => setForm({ ...form, cycleDays: e.target.value })} />
                <input className={input(isDarkMode)} type="date" value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
              </div>
              <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.prev.startDateHint')}</p>
              <select className={input(isDarkMode)} value={form.taskListGroup}
                onChange={(e) => setForm({ ...form, taskListGroup: e.target.value })}>
                {taskLists.map((l) => <option key={`${l.group}-${l.counter}`} value={l.group}>{l.group} — {l.description}</option>)}
              </select>
              <div className="flex gap-2">
                <input className={input(isDarkMode)} placeholder={t('pm.prev.equipmentOptional')} value={form.equipmentId}
                  onChange={(e) => setForm({ ...form, equipmentId: e.target.value })} />
                <select className={input(isDarkMode)} value={form.workCenter}
                  onChange={(e) => setForm({ ...form, workCenter: e.target.value })}>
                  {scope.workCenters.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <Button variant="confirm" className="!min-h-[48px]" disabled={busy || !form.description || !form.taskListGroup}
                onClick={async () => {
                  const res = await act(() => createPlan(user, {
                    description: form.description,
                    plant: me.activePlant,
                    cycleDays: Number(form.cycleDays) || 30,
                    taskListGroup: form.taskListGroup,
                    workCenter: form.workCenter,
                    ...(form.equipmentId ? { equipmentId: form.equipmentId } : {}),
                    ...(form.startDate ? { startDate: form.startDate } : {}),
                  }, crypto.randomUUID(), role));
                  if (res) { setCreating(false); setForm((f) => ({ ...f, description: '', equipmentId: '' })); }
                }}>
                {t('pm.prev.createPlan')}
              </Button>
            </div>
          )}
        </Card>
      )}

      {plans === null ? (
        <Card><p className={sub}>{t('pm.common.loading')}</p></Card>
      ) : plans.length === 0 ? (
        <Card><p className={sub}>{t('pm.prev.noPlans')}</p></Card>
      ) : plans.map((p) => {
        const inactive = /INAC|INAK/.test(p.status || '');
        const isOpen = openPlan && openPlan.planId === p.planId;
        return (
          <Card key={p.planId} className="mb-3">
            <button type="button" className="w-full text-left" onClick={() => toggleDetail(p.planId)}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{p.description}</div>
                  <div className={`text-sm mt-0.5 ${sub}`}>
                    {t('pm.prev.plan')} {p.planId}{p.status ? ` · ${p.status}` : ''}
                  </div>
                  <div className={`text-sm ${sub}`}>
                    {p.equipmentId ? `${t('pm.notif.equipment')} ${p.equipmentId} · ` : ''}{t('pm.prev.calls')}: {p.callCount}
                  </div>
                </div>
              </div>
            </button>
            {isOpen && (
              <div className="mt-3">
                {!openPlan.detail ? <p className={`text-sm ${sub}`}>{t('pm.common.loading')}</p> : (
                  <div className="space-y-1.5 text-sm">
                    {openPlan.detail.cycles.map((c, i) => (
                      <Row key={i} label={t('pm.prev.cycle')} value={t('pm.prev.everyNDays', { n: c.cycleDays })} info={t('pm.info.cycle')} />
                    ))}
                    <Row label={t('pm.prev.startDate')} value={openPlan.detail.startDate || '—'} info={t('pm.info.planStart')} />
                    {openPlan.detail.calls.length > 0 && (
                      <div className={`pt-2 ${sub}`}>
                        <p className="font-medium mb-1">{t('pm.prev.recentCalls')}</p>
                        {openPlan.detail.calls.slice(0, 6).map((c) => (
                          <div key={c.callNo} className="flex justify-between">
                            <span>#{c.callNo} · {c.plannedDate}</span>
                            <span>{c.orderId ? `${t('pm.prev.order')} ${c.orderId.replace(/^0+/, '')}` : t('pm.prev.noOrderYet')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  {canSchedule && !inactive && (
                    <Button variant="primary" className="!min-h-[48px] flex-1" disabled={busy}
                      onClick={() => act(() => schedulePlan(user, p.planId, crypto.randomUUID(), role))}>
                      <PlayCircle size={18} /> {t('pm.prev.scheduleNow')}
                    </Button>
                  )}
                  <Button variant="secondary" className="!min-h-[48px] flex-1" disabled={busy}
                    onClick={() => act(() => setPlanActive(user, p.planId, inactive, crypto.randomUUID(), role))}>
                    {inactive ? <PlayCircle size={18} /> : <PauseCircle size={18} />}
                    {inactive ? t('pm.prev.activate') : t('pm.prev.deactivate')}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

/** Task lists tab: catalogue + create with up to three operations. */
function TaskListsTab({ me, user, capabilities }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const role = me.profile.id;
  const scope = me.profile.scope;

  const [lists, setLists] = useState(null);
  const [creating, setCreating] = useState(false);
  const [desc, setDesc] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [ops, setOps] = useState([{ description: '', workCenter: scope.workCenters[0], plannedWork: '1' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const canCreate = !capabilities || capabilities.taskListCreate !== false;

  useEffect(() => {
    let alive = true;
    setLists(null);
    listTaskLists(user, me.activePlant, role)
      .then((res) => { if (alive) setLists(res.items || []); })
      .catch(() => { if (alive) setLists([]); });
    return () => { alive = false; };
  }, [user, me.activePlant, role, reloadKey]);

  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  return (
    <div>
      {error && <Card className="mb-3"><p className={isDarkMode ? 'text-rose-300 text-sm' : 'text-rose-600 text-sm'}>{error}</p></Card>}
      {canCreate ? (
        <Card className="mb-3">
          <button type="button" className={`flex items-center gap-2 font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
            onClick={() => setCreating((v) => !v)}>
            <Plus size={18} /> {t('pm.prev.newTaskList')}
          </button>
          {creating && (
            <div className="mt-3 space-y-2">
              <input className={input(isDarkMode)} placeholder={t('pm.prev.taskListDescription')} value={desc}
                onChange={(e) => setDesc(e.target.value)} />
              {/* Valid-from BEFORE the earliest plan start — the SAP date-effectivity rule. */}
              <div>
                <label className={`text-xs ${sub}`}>{t('pm.prev.validFromHint')}</label>
                <input className={input(isDarkMode)} type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
              </div>
              {ops.map((o, i) => (
                <div key={i} className="flex gap-2">
                  <input className={input(isDarkMode)} placeholder={`${t('pm.prev.operation')} ${i + 1}`} value={o.description}
                    onChange={(e) => setOps(ops.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
                  <select className={`${input(isDarkMode)} !w-36`} value={o.workCenter}
                    onChange={(e) => setOps(ops.map((x, j) => (j === i ? { ...x, workCenter: e.target.value } : x)))}>
                    {scope.workCenters.map((w) => <option key={w} value={w}>{w}</option>)}
                  </select>
                  <input className={`${input(isDarkMode)} !w-20`} type="number" min="0" step="0.5" value={o.plannedWork}
                    onChange={(e) => setOps(ops.map((x, j) => (j === i ? { ...x, plannedWork: e.target.value } : x)))} />
                </div>
              ))}
              {ops.length < 5 && (
                <button type="button" className={`text-sm underline ${sub}`}
                  onClick={() => setOps([...ops, { description: '', workCenter: scope.workCenters[0], plannedWork: '1' }])}>
                  {t('pm.prev.addOperation')}
                </button>
              )}
              <Button variant="confirm" className="!min-h-[48px]" disabled={busy || !desc}
                onClick={async () => {
                  setBusy(true); setError(null);
                  try {
                    const res = await createTaskList(user, {
                      description: desc,
                      plant: me.activePlant,
                      ...(validFrom ? { validFrom } : {}),
                      operations: ops.filter((o) => o.description).map((o) => ({
                        description: o.description, workCenter: o.workCenter, plannedWork: Number(o.plannedWork) || 1,
                      })),
                    }, crypto.randomUUID(), role);
                    if (res && res.ok === false) setError(res.error || t('pm.prev.actionFailed'));
                    else { setCreating(false); setDesc(''); setReloadKey((k) => k + 1); }
                  } catch (e) { setError(e.message); } finally { setBusy(false); }
                }}>
                {t('pm.prev.createTaskList')}
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <Card className="mb-3"><p className={`text-sm ${sub}`}>{t('pm.prev.noTaskListApi')}</p></Card>
      )}

      {lists === null ? (
        <Card><p className={sub}>{t('pm.common.loading')}</p></Card>
      ) : lists.length === 0 ? (
        <Card><p className={sub}>{t('pm.prev.noTaskLists')}</p></Card>
      ) : lists.map((l) => (
        <Card key={`${l.group}-${l.counter}`} className="mb-3">
          <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{l.description || l.group}</div>
          <div className={`text-sm mt-0.5 ${sub}`}>
            {t('pm.prev.group')} {l.group}/{l.counter} · {t('pm.prev.validFrom')} {l.validFrom || '—'}
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function PreventiveScreen({ me, user, onBack }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [tab, setTab] = useState('plans');
  const capabilities = me.sap && me.sap.capabilities; // null = unknown -> show everything

  const tabs = [
    { id: 'plans', label: t('pm.prev.tabPlans') },
    { id: 'tasklists', label: t('pm.prev.tabTaskLists') },
    { id: 'equipment', label: t('pm.prev.tabEquipment') },
    // Counters get their own tab rather than a section inside "equipment": counter-based
    // maintenance is a different job (readings, corrections, due forecast) done by different
    // people at a different rhythm than creating an equipment master record.
    { id: 'counters', label: t('pm.prev.tabCounters') },
  ];

  return (
    <div>
      <button type="button" onClick={onBack} className={`flex items-center gap-1 mb-3 text-sm font-medium ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}>
        <ArrowLeft size={16} /> {t('pm.common.back')}
      </button>
      <h2 className={`flex items-center gap-2 text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
        <CalendarClock size={20} /> {t('pm.prev.title')}
      </h2>
      <div className="flex gap-2 mb-4">
        {tabs.map((x) => (
          <button key={x.id} type="button" onClick={() => setTab(x.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium ${tab === x.id
              ? (isDarkMode ? 'bg-amber-500/20 text-amber-200 border border-amber-400/40' : 'bg-amber-100 text-amber-800 border border-amber-200')
              : (isDarkMode ? 'bg-white/[0.06] text-white/60' : 'bg-slate-100 text-slate-500')}`}>
            {x.label}
          </button>
        ))}
      </div>
      {tab === 'plans' && <PlansTab me={me} user={user} capabilities={capabilities} />}
      {tab === 'tasklists' && <TaskListsTab me={me} user={user} capabilities={capabilities} />}
      {tab === 'equipment' && <EquipmentPanel me={me} user={user} capabilities={capabilities} />}
      {tab === 'counters' && <CountersPanel me={me} user={user} capabilities={capabilities} />}
    </div>
  );
}
